import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import AuthMarketingAsideBackground from './AuthMarketingAsideBackground';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { usePublicBranding } from '../../hooks/usePublicBranding';
import { resolveBrandingLogoUrl } from '../../utils/brandingUrl';
import { JCONSULT_LOGO, JCONSULT_LOGO_WHITE } from '../../config/brandingAssets';

export const RECOVERY_STEPS = [
  { id: 1, label: 'E-mail' },
  { id: 2, label: 'Code' },
  { id: 3, label: 'Nouveau mot de passe' },
] as const;

export type RecoveryStep = (typeof RECOVERY_STEPS)[number]['id'];

const AUTH_FIELD_CLASS =
  'w-full rounded-lg border-none bg-surface-container-high py-2 pl-10 pr-3 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-0 sm:py-2.5 sm:pl-11';

type BackLink = {
  label: string;
  to: string;
};

type Props = {
  activeStep: RecoveryStep;
  icon?: string;
  iconClassName?: string;
  eyebrow?: string;
  title: string;
  description?: string;
  backLink?: BackLink;
  showStepper?: boolean;
  hint?: ReactNode;
  /** Contenu court : pas de défilement, mise en page alignée login/register. */
  compact?: boolean;
  /** En mode compact : ancrer le contenu en haut (formulaires plus longs). */
  compactTopAlign?: boolean;
  /** Toutes les étapes validées (écran de succès). */
  completed?: boolean;
  children: ReactNode;
};

export function RecoveryAlert({
  variant,
  children,
}: {
  variant: 'error' | 'success';
  children: ReactNode;
}) {
  const isError = variant === 'error';
  return (
    <div
      className={[
        'rounded-lg border px-3 py-2 text-xs sm:text-sm',
        isError
          ? 'border-error/30 bg-error-container text-on-error-container'
          : 'border-tertiary-fixed-dim/40 bg-tertiary-fixed/20 text-tertiary-container',
      ].join(' ')}
      role={isError ? 'alert' : 'status'}
    >
      {children}
    </div>
  );
}

export function RecoveryPrimaryButton({
  children,
  disabled,
  loading,
  type = 'submit',
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
}) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className="flex w-full items-center justify-center gap-2 rounded-public-btn primary-gradient px-4 py-2.5 text-sm font-bold text-white shadow-md hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? 'Traitement…' : children}
    </button>
  );
}

export function RecoveryOutlineButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="w-full rounded-public-btn border border-outline-variant/30 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function RecoveryAuthField({
  label,
  icon,
  id,
  children,
}: {
  label: string;
  icon: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1" htmlFor={id}>
      <span className="text-xs font-bold text-on-surface-variant">{label}</span>
      <div className="relative rounded-lg border border-transparent focus-glow">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <LucideSymbolIcon name={icon} tone="muted" size={18} />
        </span>
        {children}
      </div>
    </label>
  );
}

export { AUTH_FIELD_CLASS };

function RecoveryStepHeader({
  activeStep,
  completed = false,
}: {
  activeStep: RecoveryStep;
  completed?: boolean;
}) {
  return (
    <div className="mb-3 flex items-center justify-between sm:mb-4">
      <p className="text-[11px] font-bold text-on-surface-variant sm:text-xs">
        Étape {activeStep} sur {RECOVERY_STEPS.length}
      </p>
      <div className="flex items-center gap-1" aria-hidden>
        {RECOVERY_STEPS.map((step) => (
          <span
            key={step.id}
            className={`h-1.5 rounded-full transition-all ${
              completed || step.id < activeStep
                ? 'w-1.5 bg-primary/50'
                : step.id === activeStep
                  ? 'w-4 bg-primary'
                  : 'w-1.5 bg-primary/25'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function RecoveryStepper({ activeStep }: { activeStep: RecoveryStep }) {
  return (
    <nav aria-label="Étapes de réinitialisation" className="mb-6">
      <ol className="flex items-center gap-2">
        {RECOVERY_STEPS.map((step, index) => {
          const active = step.id === activeStep;
          const done = step.id < activeStep;
          return (
            <li key={step.id} className="flex min-w-0 flex-1 items-center gap-2">
              <div
                className={[
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition',
                  active
                    ? 'bg-primary text-white shadow-md shadow-primary/25'
                    : done
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-surface-container-high text-on-surface-variant',
                ].join(' ')}
              >
                {done ? <LucideSymbolIcon name="check" tone="inherit" size={16} /> : step.id}
              </div>
              <span
                className={[
                  'hidden truncate text-xs font-bold sm:inline',
                  active ? 'text-primary' : done ? 'text-emerald-700' : 'text-on-surface-variant',
                ].join(' ')}
              >
                {step.label}
              </span>
              {index < RECOVERY_STEPS.length - 1 ? (
                <span className="mx-1 hidden h-px flex-1 bg-outline-variant/30 sm:block" aria-hidden />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function PasswordRecoveryLayout({
  activeStep,
  eyebrow,
  title,
  description,
  backLink = { label: 'Retour à la connexion', to: '/login' },
  showStepper = true,
  hint,
  compact = false,
  compactTopAlign = false,
  completed = false,
  children,
}: Props) {
  const branding = usePublicBranding();

  const customLogoSrc = resolveBrandingLogoUrl(branding.logoUrl);
  const logoOnDark = customLogoSrc || JCONSULT_LOGO_WHITE;
  const logoOnLight = customLogoSrc || JCONSULT_LOGO;
  const supportEmail = branding.supportEmail;

  return (
    <main className="flex h-screen w-full overflow-hidden bg-surface text-on-surface antialiased">
      <section className="relative hidden min-w-0 lg:flex lg:h-screen lg:w-1/2">
        <AuthMarketingAsideBackground />

        <div className="relative z-10 flex h-full min-h-0 w-full flex-col p-6 text-white lg:p-8 xl:p-10">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
            <div className="flex w-full max-w-lg flex-col">
              <div className="mb-4 flex w-full justify-center">
                <img
                  src={logoOnDark}
                  alt="Logo JCONSULT"
                  className="h-auto w-[18rem] max-w-full object-contain"
                />
              </div>
              <h2 className="text-2xl font-extrabold leading-tight tracking-tight xl:text-3xl">
                Retrouvez l&apos;accès à votre espace
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-on-primary-container/90">
                Réinitialisation en trois étapes : e-mail, code, nouveau mot de passe.
              </p>

              <div className="glass-card mt-5 rounded-lg border border-white/10 p-3 sm:mt-6 sm:p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-white/80">Étapes</p>
                <ol className="mt-2 space-y-2">
                  {RECOVERY_STEPS.map((step) => {
                    const stepDone = completed || step.id < activeStep;
                    const stepReached = completed || step.id <= activeStep;
                    return (
                    <li key={step.id} className="flex items-center gap-2.5 text-sm text-on-primary-container">
                      <span
                        className={[
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                          stepReached ? 'bg-white/25 text-white' : 'bg-white/10 text-white/70',
                        ].join(' ')}
                      >
                        {stepDone ? (
                          <LucideSymbolIcon name="check" tone="inherit" size={12} />
                        ) : (
                          step.id
                        )}
                      </span>
                      <span className={stepReached ? 'font-semibold text-white' : 'text-white/75'}>
                        {step.label}
                      </span>
                    </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          </div>

          <div
            className={[
              'flex w-full shrink-0 items-center justify-between gap-3 pt-3 sm:pt-4',
              completed ? '' : 'border-t border-white/10',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <p className="text-[11px] text-on-primary-container/80">
              Besoin d&apos;aide ?{' '}
              <a href={`mailto:${supportEmail}`} className="font-semibold text-white hover:underline">
                Support
              </a>
              {' · '}
              <Link to="/legal/cgu" className="font-semibold text-white hover:underline">
                CGU
              </Link>
              {' · '}
              <Link to="/legal/confidentialite" className="font-semibold text-white hover:underline">
                Confidentialité
              </Link>
            </p>
            <LucideSymbolIcon name="shield" tone="inherit" className="text-on-primary-container/70" size={18} />
          </div>
        </div>
      </section>

      <section className="flex h-screen min-h-0 min-w-0 w-full flex-1 flex-col bg-surface-container-lowest lg:w-1/2">
        <div className="mx-auto w-full max-w-xl shrink-0 px-4 pt-5 sm:px-6 sm:pt-6">
          <Link
            to={backLink.to}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline sm:text-sm"
          >
            ← {backLink.label}
          </Link>
        </div>

        <div
          className={[
            'mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col justify-center px-4 sm:px-6',
            compact
              ? [
                  'overflow-hidden pb-3 sm:pb-4',
                  compactTopAlign ? 'justify-start pt-1 sm:pt-2' : 'justify-center',
                ].join(' ')
              : 'overflow-y-auto pb-4 sm:pb-6',
          ].join(' ')}
        >
          <div
            className={[
              'flex w-full shrink-0 items-center justify-center lg:hidden',
              compact ? 'relative -top-3 mb-2 sm:-top-4' : 'mb-3',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <img
              src={logoOnLight}
              alt="Logo JCONSULT"
              className="mx-auto h-auto w-28 object-contain sm:w-32"
            />
          </div>
          <div className={compact ? 'mb-4 shrink-0 text-left sm:mb-5' : 'mb-5 shrink-0 text-left sm:mb-6'}>
            {eyebrow ? (
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-primary sm:text-[11px]">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="relative -top-3 text-xl font-black tracking-tight text-on-surface sm:-top-3.5 sm:text-2xl">
              {title}
            </h1>
            {description ? (
              <p className="mt-1.5 text-xs leading-relaxed text-on-surface-variant sm:mt-2 sm:text-sm">
                {description}
              </p>
            ) : null}
          </div>

          <div className={compact ? 'space-y-3' : 'space-y-4'}>
            {showStepper ? (
              compact ? (
                <RecoveryStepHeader activeStep={activeStep} completed={completed} />
              ) : (
                <RecoveryStepper activeStep={activeStep} />
              )
            ) : null}

            {children}

            {hint && !compact ? (
              <div className="rounded-lg border border-outline-variant/20 bg-surface-container-low px-3 py-2.5 text-xs text-on-surface-variant">
                {hint}
              </div>
            ) : null}
          </div>
        </div>

        <p className="mx-auto w-full max-w-xl shrink-0 px-4 pb-4 text-center text-[11px] text-on-surface-variant sm:px-6 sm:text-xs lg:hidden">
          Besoin d&apos;aide ?{' '}
          <a href={`mailto:${supportEmail}`} className="font-semibold text-primary hover:underline">
            {supportEmail}
          </a>
        </p>
      </section>
    </main>
  );
}
